/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useRef, useState } from "react";
import { BEM, IIconPackageURLMapping } from "@adapptio/lib-components";
import { FieldWrapper } from "../FieldWrapper";
import { IFieldValidationError } from "../FieldTypes";
import { FC_ANCHOR, FC_DIRECTION, Icon, ICON_SIZE, IconList, InputAdornment, InputButton } from "../../core";
import { useEscKey } from "../../../hooks";
import {
	FIELD_FLOATING_PANEL_SCHEMA,
	FIELD_FLOATING_PANEL_STYLE,
	FieldFloatingPanel
} from "../FieldFloatingPanel";
import { isFunction, isNonEmptyArray } from "@adapptio/lib-shared";

import "./FieldIconSelector.scss";
import { ResourceInput } from "../../core/ResourceInput";
import { FieldStatus } from "../FieldStatus";

export interface IIconListItem {
	name: string; // searchable
	title?: string; // searchable, displayed as icon tooltip, if not provided, tooltip has fallback to name
	tags?: string[]; // searchable
}

export interface IIconListPack {
	name: string;
	label: string; // displayed as a tab tooltip
	shortLabel: string; // displayed as tab label
	icons: IIconListItem[];
}

export interface IFieldIconSelectorProps {
	id?: string;
	value: string;
	iconPacks: IIconListPack[];
	urlMapping?: IIconPackageURLMapping;
	placeholder?: string;
	readOnly?: boolean;
	validationErrors?: IFieldValidationError[];
	className?: string;
	grow?: boolean;
	listAnchor?: FC_ANCHOR;
	listDirection?: FC_DIRECTION;
	listOffset?: number;
	onChange?: (value: string) => void;
	onClear?: () => void;
	clearable?: boolean;
}

export const FieldIconSelector: React.FC<IFieldIconSelectorProps> = ({
	id,
	value,
	iconPacks,
	urlMapping,
	placeholder,
	readOnly,
	validationErrors,
	className,
	grow,
	listAnchor,
	listDirection,
	listOffset,
	onChange,
	onClear,
	clearable
}) => {
	const baseClass = BEM("edt-field-icon-selector");

	const adornment = validationErrors?.length > 0 || clearable;

	const [ isListOpened, setIsListOpened ] = useState(false);
	const elRef = useRef<HTMLDivElement>();

	const toggleIconList = useCallback<React.MouseEventHandler>((event) => {
		event.preventDefault();
		setIsListOpened((x) => !x);
	}, []);

	const closeIconList = useCallback(() => {
		setIsListOpened(false);
	}, []);

	const handleSelect = useCallback(
		(packName: string, iconName: string) => {
			onChange(iconName);
			setIsListOpened(false);
		},
		[ onChange ]
	);

	const handleClearClick = useCallback(() => {
		if (isFunction(onChange)) {
			onChange(null);
		}
	}, [ onChange ]);

	useEscKey(() => setIsListOpened(false), isListOpened);

	const [ selectedPackName ] = (value || "").split("/", 2);

	if (className) {
		baseClass.add(className);
	}

	return (
		<FieldWrapper type="icon-selector">
			<div className={baseClass.toClassName()}>
				<ResourceInput
					className={baseClass.element("selector").toClassName()}
					onClick={toggleIconList}
					disabled={readOnly}
					ref={elRef}
					tooltip={value || placeholder}
					endAdornment={
						adornment && (
							<InputAdornment>
								{isNonEmptyArray(validationErrors) && (
									<FieldStatus errors={validationErrors} />
								)}
								{clearable && (
									<InputButton
										className={baseClass.element("editor-clear").toClassName()}
										onClick={handleClearClick}
										tabIndex={-1}
										disabled={!value}
									>
										<Icon
											className={baseClass.element("editor-clear-icon").toClassName()}
											name="mdi/close"
										/>
									</InputButton>
								)}
							</InputAdornment>
						)
					}
				>
					{value ?? placeholder}
				</ResourceInput>

				<FieldFloatingPanel
					for={elRef}
					isOpened={isListOpened}
					anchor={listAnchor || FC_ANCHOR.RIGHT_BOTTOM}
					direction={listDirection || FC_DIRECTION.HR}
					style={FIELD_FLOATING_PANEL_STYLE.STICKY}
					schema={FIELD_FLOATING_PANEL_SCHEMA.PRIMARY}
					offset={listOffset || 0}
					className="edt-field--type-icon-selector__panel"
					onClose={closeIconList}
				>
					<IconList
						iconPacks={iconPacks}
						urlMapping={urlMapping}
						iconPreviewSize={ICON_SIZE.MEDIUM}
						selectedPackName={selectedPackName}
						selectedIconName={value}
						onSelect={handleSelect}
					/>
				</FieldFloatingPanel>
			</div>
		</FieldWrapper>
	);
};
